/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import { tableV2StackedCellContentSx } from '../../../components/v2/tableV2Styles';
import * as actions from '../../../store/controleFrequencia/actions';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import * as selectorsAuth from '../../../store/auth/reducer';
import { FOLHA_PONTO_FULL_ACCESS, JORNADA } from 'constants/modules';
import { hasModuleConfiguration } from '../../../helpers/moduleConfigurationPermission';
import { formatWithDay } from 'utils/date-format';
import { getJornadaHoraExtraDisplay } from '../../../helpers/jornadaValidado';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import Formulario from './Formulario';

moment.locale('pt-br');

const hasPermission = (moduleConfigurations, authData) =>
  hasModuleConfiguration(moduleConfigurations, authData, FOLHA_PONTO_FULL_ACCESS);

const getDefaultFilter = (moduleConfigurations, authData) => ({
  inicio: moment().subtract(1, 'month').startOf('month').valueOf(),
  fim: moment().endOf('month').valueOf(),
  tipoControle: 'FOLHA_PONTO',
  usuarioId: !hasPermission(moduleConfigurations, authData) ? [authData.id] : [],
});

const FolhaPontoV2View = ({
  authData,
  exportControleFrequencia,
  deleteControleFrequencia,
  moduleConfigurations,
  history,
}) => {
  const useNovoFluxo = hasModuleConfiguration(moduleConfigurations, authData, JORNADA);

  useEffect(() => {
    if (useNovoFluxo) {
      history.replace('/v2/jornada');
    }
  }, [useNovoFluxo, history]);

  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [localFilter, setLocalFilter] = useState(() =>
    getDefaultFilter(moduleConfigurations, authData));
  const [openExport, setOpenExport] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);

  const adminPermission = hasPermission(moduleConfigurations, authData);

  const defaultFilterMemo = useMemo(
    () => getDefaultFilter(moduleConfigurations, authData),
    [moduleConfigurations, authData],
  );

  const apiFilter = useMemo(
    () => ({ ...localFilter, tipoControle: 'FOLHA_PONTO' }),
    [localFilter],
  );

  const onEdit = (rowId) => {
    setOpen(true);
    setId(rowId);
  };

  const onAdd = () => {
    setOpen(true);
    setId(null);
  };

  const onClose = (isSave) => {
    setOpen(false);
    setId(null);
    if (isSave) {
      setReload((current) => !current);
    }
  };

  const rowActions = [
    {
      key: 'view',
      icon: <VisibilityOutlinedIcon fontSize="small" />,
      title: 'Visualizar',
      onClick: (rowId) => onEdit(rowId),
      show: (row) => !adminPermission && !row.canEdit,
    },
    {
      key: 'edit',
      icon: <EditOutlinedIcon fontSize="small" />,
      title: 'Editar',
      onClick: (rowId) => onEdit(rowId),
      show: (row) => adminPermission || row.canEdit,
    },
  ];

  const columns = [
    {
      field: 'data',
      headerName: 'Data',
      flex: 0.5,
      renderCell: (params) => formatWithDay(params.row.data),
    },
    { field: 'ocorrenciaNome', headerName: 'Ocorrência', flex: 0.6 },
    {
      field: 'inspetorNome',
      headerName: 'Inspetor e Auxiliar',
      flex: 0.8,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
            Insp:
          </Typography>
          <Typography variant="body2" component="span">
            {params.row.inspetorNome}
          </Typography>
          {params.row.inspetorAuxiliarNome && (
            <>
              <br />
              <Typography variant="body2" component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
                Insp. Aux:
              </Typography>
              <Typography variant="body2" component="span">
                {params.row.inspetorAuxiliarNome}
              </Typography>
            </>
          )}
        </Box>
      ),
    },
    { field: 'jornadaTotal', headerName: 'Total da Jornada', flex: 0.5 },
    {
      field: 'horaExtraAutorizadoPorNome',
      headerName: 'Hora Extra',
      flex: 0.6,
      renderCell: (params) => {
        const display = getJornadaHoraExtraDisplay(params.row);
        return (
          <Box sx={{ ...tableV2StackedCellContentSx, flexDirection: 'row', alignItems: 'center', gap: 1 }}>
            {renderStatus(display)}
            {display.autorizadoPorNome ? (
              <Typography variant="body2" noWrap>
                {display.autorizadoPorNome}
              </Typography>
            ) : null}
          </Box>
        );
      },
    },
  ];

  if (useNovoFluxo) return null;

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="h4">Folha de Pontos</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="pontos/filter"
          paginationUrl="/v2/folha-pontos"
          filterData={apiFilter}
          refreshKey={reload}
          columns={columns}
          rowHeight={56}
          actions={rowActions}
          onDelete={(rowId) => deleteControleFrequencia(rowId, () => setReload((c) => !c))}
          canDelete={(row) => adminPermission || row.canEdit}
          headerActions={[
            {
              label: 'Novo',
              onClick: onAdd,
              startIcon: <AddIcon />,
              variant: 'contained',
            },
            {
              label: 'Exportar',
              onClick: () => setOpenExport(true),
              startIcon: <FileDownloadOutlinedIcon />,
              variant: 'outlined',
            },
          ]}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilterMemo}
          appliedFilter={localFilter}
          fields={[
            { field: 'useDates' },
            {
              field: 'useUsuarios',
              md: 12,
              xs: 12,
              multiSelect: true,
              disabled: !adminPermission,
            },
            {
              field: 'useOption',
              md: 12,
              xs: 12,
              code: 'PONTO_OCORRENCIA',
              label: 'Ocorrências',
              key: 'ocorrenciaIds',
            },
          ]}
          onFilter={(filterData) => {
            const next = { ...filterData, tipoControle: 'FOLHA_PONTO' };
            setLocalFilter(next);
            setOpenFilter(false);
          }}
        />

        <Formulario
          id={id}
          open={open}
          onClose={onClose}
          adminPermission={adminPermission}
        />

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => exportControleFrequencia(apiFilter, () => setOpenExport(false))}
          title="Confirmação"
          confirmMessage="Exportar"
        >
          <DialogContentText>
            Deseja exportar todas as informações filtradas?
          </DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

FolhaPontoV2View.propTypes = {
  authData: PropTypes.object,
  exportControleFrequencia: PropTypes.func.isRequired,
  deleteControleFrequencia: PropTypes.func.isRequired,
  moduleConfigurations: PropTypes.array,
  history: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...actions,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(FolhaPontoV2View));
